import { z } from 'zod';

/**
 * Therapist self-service profile — GET/PATCH /api/v1/therapist/profile
 * (THERAPIST only; admins curate the same fields via
 * PATCH /api/v1/admin/therapists/:id/profile).
 *
 * Three distinct taxonomies, deliberately (ADR-015 §6):
 *  - `specializationSlugs`: the admin-managed Specialization categories
 *    (matching engine + landing filter chips). A therapist picks from the
 *    existing list, they can't invent new ones.
 *  - `caseTagsEl`/`caseTagsEn`: free-form "περιστατικά που αναλαμβάνω"
 *    chips the therapist writes themselves (e.g. "κρίσεις πανικού",
 *    "πένθος") — bilingual, hand-written in both languages, never
 *    machine-translated.
 *  - `languagesSpoken`: which language(s) the therapist can hold a
 *    SESSION in (a matching filter) — unrelated to which language(s) the
 *    profile TEXT below is written in.
 *
 * `headlineEl`/`bioEl` are the mandatory Greek version; `headlineEn`/
 * `bioEn` are optional. A viewer who requests the English profile and
 * finds the `*En` fields empty is shown the Greek text with a
 * "not yet translated" indicator client-side — never a blank card.
 */

export const TherapistProfileCompletenessSchema = z.object({
  el: z.object({ headline: z.boolean(), bio: z.boolean() }),
  en: z.object({ headline: z.boolean(), bio: z.boolean() }),
});
export type TherapistProfileCompleteness = z.infer<typeof TherapistProfileCompletenessSchema>;

export const TherapistOwnProfileSchema = z.object({
  therapistProfileId: z.string().cuid(),
  status: z.enum(['PENDING_REVIEW', 'APPROVED', 'SUSPENDED', 'REJECTED']),
  headlineEl: z.string().nullable(),
  headlineEn: z.string().nullable(),
  bioEl: z.string().nullable(),
  bioEn: z.string().nullable(),
  photoMediaId: z.string().nullable(),
  photoUrl: z.string().nullable(),
  introVideoUrl: z.string().nullable(),
  caseTagsEl: z.array(z.string()),
  caseTagsEn: z.array(z.string()),
  /** ISO 639-1 codes — languages the therapist can hold a session in, e.g. ["el","en"]. */
  languagesSpoken: z.array(z.string()),
  specializationSlugs: z.array(z.string()),
  completeness: TherapistProfileCompletenessSchema,
});
export type TherapistOwnProfile = z.infer<typeof TherapistOwnProfileSchema>;

export const TherapistOwnProfileResponseSchema = z.object({
  profile: TherapistOwnProfileSchema,
});
export type TherapistOwnProfileResponse = z.infer<typeof TherapistOwnProfileResponseSchema>;

/** Every field: omit to leave untouched, explicit `null` to clear (text fields only — arrays clear via `[]`). A non-null string must be non-empty. */
export const UpdateOwnProfileRequestSchema = z.object({
  headlineEl: z.string().min(1).max(200).nullable().optional(),
  headlineEn: z.string().min(1).max(200).nullable().optional(),
  bioEl: z.string().min(1).max(5000).nullable().optional(),
  bioEn: z.string().min(1).max(5000).nullable().optional(),
  photoMediaId: z.string().nullable().optional(),
  introVideoUrl: z.string().url().max(500).nullable().optional(),
  caseTagsEl: z.array(z.string().min(1).max(60)).max(20).optional(),
  caseTagsEn: z.array(z.string().min(1).max(60)).max(20).optional(),
  /** ISO 639-1 codes, e.g. ["el","en"]. */
  languagesSpoken: z.array(z.string().min(2).max(5)).max(10).optional(),
  /** Must reference existing, active Specialization slugs. */
  specializationSlugs: z.array(z.string().min(1).max(60)).max(10).optional(),
});
export type UpdateOwnProfileRequest = z.infer<typeof UpdateOwnProfileRequestSchema>;
